import * as THREE from 'three';

import { CloudUniforms } from './Clouds';
import { CLOUD_GLSL, CLOUD_HEIGHT } from './CloudNoise';
import { SkyState } from './Daylight';
import { WeatherState } from './Weather';

/**
 * Deszcz: smugi lecące spod chmur.
 *
 * **Pada tam, gdzie jest chmura, i nigdzie indziej.** Każda kropla pyta
 * `cloudAt` — tej samej funkcji GLSL, z której rysuje się warstwa nad światem
 * i jej cień — w miejscu, w którym się rodzi. Wyrwa w pokrywie zostaje sucha
 * i widać to gołym okiem; bez tego deszcz byłby filtrem na obrazie, a nie
 * pogodą nad krajobrazem.
 *
 * **Ile pada, bierze się z zachmurzenia**, czyli z czystej funkcji zegara
 * serwera — dwóch graczy obok siebie widzi ten sam deszcz nad tym samym polem,
 * bez jednego bajtu w protokole. Fazy pojedynczych kropel nie synchronizuje nic
 * i nie ma po co: zgadzać się musi to, **czy** pada, a nie to, gdzie akurat
 * jest ta jedna kreska.
 *
 * **Cała animacja siedzi w vertex shaderze.** Geometria powstaje raz i nigdy
 * się nie zmienia; klatka to jeden uniform czasu. Przesuwanie dwóch tysięcy
 * kropel po stronie CPU byłoby przepisywaniem bufora co klatkę za coś, co jest
 * jednym `fract`.
 */

/**
 * Ile smug jest w powietrzu.
 *
 * Pudło jedzie za kamerą **i skaluje się jej zasięgiem**, więc ta liczba jest
 * gęstością na ekranie, a nie na świecie: przy każdym przybliżeniu widać mniej
 * więcej tyle samo kresek. Stałe pudło dawałoby przy zoomie 4 szesnastokrotnie
 * rzadszy deszcz, bo widok kurczy się z kwadratem przybliżenia.
 */
const DROPS = 2200;

/** Ile razy na sekundę kropla przelatuje całe pudło, i ile się to waha. */
const SPEED = 0.42;
const SPEED_SPREAD = 0.35;

/** Jak długa jest smuga, w jednostkach świata. Heks ma 0,5 promienia. */
const LENGTH = 0.7;

/** O ile w bok znosi kroplę na każdą jednostkę spadku. */
const SLANT = 0.30;

/** Najgęstszy deszcz, jaki wolno narysować. Wyżej robi się z tego mgła. */
const OPACITY = 0.5;

/**
 * Jak wysokie jest pudło względem zasięgu widoku — i sufit na tej wysokości.
 *
 * Sufitem jest warstwa chmur, bo **stamtąd** deszcz leci; przy oddalonej
 * kamerze pudło sięga dokładnie do niej. Przybliżona kamera nie widzi ani
 * chmur (od zoomu 2 warstwy nie ma), ani górnych trzech czwartych spadku, więc
 * trzymanie tam kropel byłoby wyrzucaniem ich poza kadr — pudło zsiada wtedy do
 * pasa, który widać.
 */
const HEIGHT_OF_REACH = 1.4;

/** Ile widoku ma obejmować pudło w poziomie, w wielokrotnościach zasięgu. */
const SPREAD_OF_REACH = 2.2;

/**
 * Ile deszczu zostaje w nocy.
 *
 * Nie gaśnie razem ze słońcem, bo nie jest cieniem: cień bez światła nie
 * istnieje, a deszcz w nocy pada dalej i widać go w tym, co świeci.
 */
const NIGHT_FLOOR = 0.4;

export class Rain {
  readonly object: THREE.LineSegments;
  private readonly material: THREE.ShaderMaterial;
  /**
   * Własny zegar, całkowany klatkami.
   *
   * Nie zegar świata: ten idzie w sekundach od początku i po godzinach gry
   * `fract(t * prędkość)` zaczyna gubić precyzję w `float`, czyli deszcz
   * zaczyna skakać. Nie ma czego tym synchronizować — patrz nagłówek pliku.
   */
  private time = 0;

  constructor(clouds: CloudUniforms) {
    const positions = new Float32Array(DROPS * 2 * 3);
    const ends = new Float32Array(DROPS * 2);
    const speeds = new Float32Array(DROPS * 2);

    for (let drop = 0; drop < DROPS; drop += 1) {
      // Miejsce w pudle jako ułamek jego boku, nie w jednostkach świata: bok
      // zmienia się z przybliżeniem, więc rozstaw musi być względny.
      const x = Math.random();
      const z = Math.random();
      const phase = Math.random();
      const speed = SPEED * (1 + (Math.random() - 0.5) * SPEED_SPREAD);

      for (let end = 0; end < 2; end += 1) {
        const at = (drop * 2 + end) * 3;
        positions[at] = x;
        positions[at + 1] = phase;
        positions[at + 2] = z;
        ends[drop * 2 + end] = end;
        speeds[drop * 2 + end] = speed;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aEnd', new THREE.BufferAttribute(ends, 1));
    geometry.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      // Widziany przez teren: góra zasłania deszcz za nią, bo bufor głębi już
      // to wie. Do bufora nic nie pisze — smuga nie może zasłonić niczego, co
      // narysuje się po niej.
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uFocus: { value: new THREE.Vector2() },
        uSpread: { value: 80 },
        uTop: { value: CLOUD_HEIGHT },
        uHeight: { value: CLOUD_HEIGHT },
        uLength: { value: LENGTH },
        uSlant: { value: new THREE.Vector2() },
        uOpacity: { value: 0 },
        uColor: { value: new THREE.Color(0xffffff) },
        // Te same obiekty uniformów, którymi jadą chmury — nie kopie. Kropla
        // i chmura muszą pytać jednego pola szumu, przesuniętego o to samo.
        uCloudOffset: clouds.uCloudOffset,
        uCloudScale: clouds.uCloudScale,
        uCloudCover: clouds.uCloudCover,
      },
      vertexShader: /* glsl */ `
        uniform float uTime;
        uniform vec2 uFocus;
        uniform float uSpread;
        uniform float uTop;
        uniform float uHeight;
        uniform float uLength;
        uniform vec2 uSlant;
        uniform float uOpacity;
        uniform vec2 uCloudOffset;
        uniform float uCloudScale;
        uniform float uCloudCover;
        attribute float aEnd;
        attribute float aSpeed;
        varying float vAlpha;
        ${CLOUD_GLSL}

        void main() {
          vec2 born = position.xz * uSpread;
          float phase = position.y;

          // Ile ta kropla ma już za sobą. Ogon smugi jest tam, gdzie głowa była
          // przed chwilą — liczony tym samym wzorem, więc smuga leży wzdłuż
          // toru lotu i skos wiatru wychodzi z niej sam.
          float fallen = fract(uTime * aSpeed + phase) * uHeight - aEnd * uLength;
          float y = uTop - fallen;

          // Pudło owinięte na punkcie skupienia: kropla, która wyszła bokiem,
          // wraca drugą stroną. Przeskok jest o cały bok pudła, a pudło jest
          // szersze od kadru, więc nie ma go jak zobaczyć.
          vec2 anchor = uFocus - uSpread * 0.5;
          vec2 world = anchor + mod(born + uSlant * fallen - anchor, uSpread);

          // Pytane w miejscu narodzin, a nie tam, gdzie kropla jest teraz:
          // chmura wypuściła ją raz i to ona rozstrzyga, czy ta kropla istnieje.
          // Liczone gdzie indziej migotałoby przy każdym drgnięciu wiatru.
          float overhead = cloudAt(born, uCloudOffset, uCloudScale, uCloudCover);

          // Brzeg pudła wygaszony, żeby deszcz nie kończył się ścianą.
          float away = distance(world, uFocus);
          float near = 1.0 - smoothstep(uSpread * 0.34, uSpread * 0.5, away);

          vAlpha = uOpacity * overhead * near;
          gl_Position = projectionMatrix * viewMatrix * vec4(world.x, y, world.y, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying float vAlpha;
        void main() {
          if (vAlpha < 0.01) discard;
          gl_FragColor = vec4(uColor, vAlpha);
        }
      `,
    });

    this.object = new THREE.LineSegments(geometry, this.material);
    this.object.frustumCulled = false;
    // Pod chmurami, bo pod nimi leci: warstwa nad światem ma 5.
    this.object.renderOrder = 4;
  }

  /**
   * Czy gracz chce deszczu — osobno od tego, czy akurat pada.
   *
   * Sam obiekt gaśnie przy suchej pogodzie, żeby nie przepuszczać przez vertex
   * shader 4400 wierzchołków po to, by fragment shader je odrzucił. Bez tej
   * pary „wyłączony" i „nie pada" byłyby jednym stanem, a przełącznik po
   * przejściu deszczu zapalałby się sam.
   */
  private wanted = true;

  set visible(value: boolean) {
    this.wanted = value;
    if (!value) this.object.visible = false;
  }

  get visible(): boolean {
    return this.wanted;
  }

  /**
   * Jedna klatka deszczu.
   *
   * `reach` to zasięg widoku kamery na ziemi — ten sam, którym mierzy się mgłę.
   * Z niego bierze się i bok pudła, i jego wysokość, więc deszcz zagęszcza się
   * przy przybliżaniu zamiast rzednąć.
   */
  update(
    weather: WeatherState,
    sky: SkyState,
    focus: THREE.Vector3,
    reveal: number,
    reach: number,
    deltaTime: number
  ): void {
    this.object.visible = this.wanted && weather.rain > 0 && reveal > 0;
    if (!this.object.visible) return;

    this.time += deltaTime;

    const uniforms = this.material.uniforms;
    const spread = Math.max(12, reach * SPREAD_OF_REACH);
    // Sufitem jest warstwa chmur — stamtąd leci — a przy przybliżonej kamerze
    // pas zsiada do tego, co widać.
    const height = Math.min(CLOUD_HEIGHT, Math.max(6, reach * HEIGHT_OF_REACH));

    uniforms.uTime.value = this.time;
    uniforms.uFocus.value.set(focus.x, focus.z);
    uniforms.uSpread.value = spread;
    uniforms.uTop.value = focus.y + height;
    uniforms.uHeight.value = height;
    uniforms.uSlant.value.set(weather.slantX * SLANT, weather.slantZ * SLANT);

    // Kolor mgły, rozjaśniony: smuga ma być jaśniejsza od tego, co za nią, i to
    // samo w nocy — deszcz nie ma własnej barwy, tylko odbija to, co świeci.
    uniforms.uColor.value
      .copy(sky.fogColor)
      .lerp(sky.night ? sky.skyColor : sky.sunColor, 0.5)
      .lerp(WHITE, 0.25);

    const light = NIGHT_FLOOR + (1 - NIGHT_FLOOR) * sky.shadowStrength;
    uniforms.uOpacity.value = OPACITY * weather.rain * reveal * light;
  }

  dispose(): void {
    this.object.geometry.dispose();
    this.material.dispose();
  }
}

const WHITE = new THREE.Color(0xffffff);
